<template>
  <Sheet :open="props.open" @update:open="emit('update:open', $event)">
    <SheetContent class="w-[90vw] max-w-md p-0 flex flex-col" side="left">
      <SheetHeader class="p-4 border-b border-gray-200">
        <SheetTitle class="flex items-center">
          <Filter class="h-5 w-5 mr-2 text-primary" />
          Filter Vehicles
        </SheetTitle>
        <SheetDescription>
          Select options to refine your search.
        </SheetDescription>
      </SheetHeader>
      
      <ScrollArea class="grow p-4">
        <!-- Mobile Filter Sections -->
        <div class="space-y-6">
          <!-- Vehicle Type -->
          <Collapsible v-model:open="mobileSectionsOpen.body" class="border rounded-lg overflow-hidden">
            <CollapsibleTrigger class="flex justify-between items-center w-full p-3 bg-gray-50 hover:bg-gray-100 transition-colors">
               <span class="font-medium truncate">Vehicle Type</span>
               <div class="flex items-center">
                 <template v-if="props.getSelectedBodyType">
                   <span class="text-sm text-gray-500 mr-2">({{ props.getSelectedBodyType }})</span>
                 </template>
                 <template v-else>
                   <span class="px-2 py-0.5 rounded-full bg-gray-200 text-xs font-medium text-gray-700 mr-2">{{ props.bodyTypesCount }}</span>
                 </template>
                 <ChevronDown class="h-4 w-4 transition-transform duration-200 text-gray-500" :class="{'rotate-180': mobileSectionsOpen.body}" />
               </div>
            </CollapsibleTrigger>
            <CollapsibleContent class="p-3 space-y-2">
               <Button 
                  variant="ghost" 
                  @click="emit('clear-filter', 'body')"
                  class="w-full justify-start h-auto py-2 px-3 text-left"
                  :class="{ 'bg-primary/10 text-primary font-semibold': !props.getSelectedBodyType }"
                >
                  <div>
                    <div>All Types <span class="text-xs text-gray-500">({{ props.bodyTypesCount }})</span></div>
                  </div>
               </Button>
               <Button 
                  v-for="option in props.bodyTypes" 
                  :key="option.value"
                  variant="ghost" 
                  @click.stop.prevent="emit('toggle-filter', { filterName: 'body', itemValue: option.value })" 
                  class="w-full justify-between h-auto py-2 px-3 text-left"
                  :class="{ 'bg-primary/10 text-primary font-semibold': props.isBodyTypeSelected(option.value) }"
               >
                  <span>{{ option.displayValue }}</span>
                  <span class="bg-gray-100 text-xs font-medium px-2 py-1 rounded-full text-gray-600 ml-2">
                    {{ props.getBodyTypeCount(option.value) }}
                  </span>
               </Button>
            </CollapsibleContent>
          </Collapsible>

          <!-- Make -->
          <Collapsible v-model:open="mobileSectionsOpen.make" class="border rounded-lg overflow-hidden">
            <CollapsibleTrigger class="flex justify-between items-center w-full p-3 bg-gray-50 hover:bg-gray-100 transition-colors">
               <span class="font-medium truncate">Make</span>
               <div class="flex items-center">
                 <template v-if="props.getSelectedMake">
                   <span class="text-sm text-gray-500 mr-2">({{ props.getSelectedMake }})</span>
                 </template>
                 <template v-else>
                   <span class="px-2 py-0.5 rounded-full bg-gray-200 text-xs font-medium text-gray-700 mr-2">{{ props.makesCount }}</span>
                 </template>
                 <ChevronDown class="h-4 w-4 transition-transform duration-200 text-gray-500" :class="{'rotate-180': mobileSectionsOpen.make}" />
               </div>
            </CollapsibleTrigger>
            <CollapsibleContent class="p-3 space-y-2">
               <Button 
                  variant="ghost" 
                  @click="emit('clear-filter', 'make')"
                  class="w-full justify-start h-auto py-2 px-3 text-left"
                  :class="{ 'bg-primary/10 text-primary font-semibold': !props.getSelectedMake }"
                >
                  All Makes <span class="text-xs text-gray-500">({{ props.makesCount }})</span>
                </Button>
               <Button 
                  v-for="option in props.makes" 
                  :key="option.value"
                  variant="ghost" 
                  @click="emit('toggle-filter', { filterName: 'make', itemValue: option.value })"
                  class="w-full justify-between h-auto py-2 px-3 text-left flex items-center"
                  :class="{ 'bg-primary/10 text-primary font-semibold': props.isMakeSelected(option.value) }"
                >
                  <div class="flex items-center gap-2 overflow-hidden">
                    <div class="w-5 h-5 bg-white rounded-full flex items-center justify-center overflow-hidden border border-gray-200 shrink-0">
                      <img :src="typeof option.value === 'string' ? `https://driveagent.b-cdn.net/files/shared/badges/${option.value.toLowerCase()}.png` : 'https://driveagent.b-cdn.net/files/shared/badges/default.png'" :alt="option.displayValue" class="max-w-full max-h-full object-contain" onerror="this.src='https://driveagent.b-cdn.net/files/shared/badges/default.png'; this.onerror=null;"/>
                    </div>
                    <span class="truncate">{{ option.displayValue }}</span>
                  </div>
                  <span class="bg-gray-100 text-xs font-medium px-2 py-1 rounded-full text-gray-600 ml-2 shrink-0">
                    {{ props.getMakeCount(option.value) }}
                  </span>
               </Button>
            </CollapsibleContent>
          </Collapsible>

          <!-- Model -->
          <Collapsible v-model:open="mobileSectionsOpen.model" class="border rounded-lg overflow-hidden">
            <CollapsibleTrigger class="flex justify-between items-center w-full p-3 bg-gray-50 hover:bg-gray-100 transition-colors">
               <span class="font-medium truncate">Model</span>
               <div class="flex items-center">
                 <template v-if="props.getSelectedModel">
                   <span class="text-sm text-gray-500 mr-2">({{ props.getSelectedModel }})</span>
                 </template>
                 <template v-else>
                   <span class="px-2 py-0.5 rounded-full bg-gray-200 text-xs font-medium text-gray-700 mr-2">{{ props.modelsCount }}</span>
                 </template>
                 <ChevronDown class="h-4 w-4 transition-transform duration-200 text-gray-500" :class="{'rotate-180': mobileSectionsOpen.model}" />
               </div>
            </CollapsibleTrigger>
            
            <CollapsibleContent class="p-3 space-y-2">
              <Button
                variant="ghost"
                @click="emit('clear-filter', 'model'); emit('update:modelSearchKeyword', '')"
                class="w-full justify-start h-auto py-2 px-3 text-left"
                :class="{ 'bg-primary/10 text-primary font-semibold': !props.getSelectedModel }"
              >
                All Models <span class="text-xs text-gray-500">({{ props.modelsCount }})</span>
              </Button>

              <div class="relative my-2">
                <Input
                  :modelValue="props.modelSearchKeyword"
                  @update:modelValue="emit('update:modelSearchKeyword', $event)"
                  class="w-full pl-9 pr-8 mb-4 border-gray-200 focus:border-primary focus:ring-primary rounded-lg bg-gray-50 transition-all duration-200 h-9"
                  placeholder="Search models..."
                  @keyup.enter="emit('handle-model-search')"
                />
                <span class="absolute start-0 inset-y-0 flex items-center justify-center px-2">
                  <Search class="size-5 text-muted-foreground" />
                </span>
                <div v-if="props.modelSearchKeyword" class="absolute inset-y-0 right-2 flex items-center">
                  <button
                    @click="() => { emit('update:modelSearchKeyword', ''); emit('clear-filter', 'model'); }"
                    type="button"
                    class="text-gray-400 hover:text-gray-600 transition-colors duration-200 focus:outline-none"
                    aria-label="Clear search"
                  >
                    <X class="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div class="space-y-1 pr-1">
                <template v-if="props.modelSearchKeyword?.trim()">
                  <div v-if="props.globallyFilteredModels.length > 0">
                    <Button
                      v-for="model in props.globallyFilteredModels"
                      :key="model.value"
                      variant="ghost"
                      @click="emit('toggle-filter', { filterName: 'model', itemValue: model.value })"
                      class="w-full justify-between h-auto py-2 px-3 text-left flex items-center"
                      :class="{ 'bg-primary/10 text-primary font-semibold': props.isModelSelected(model.value) }"
                    >
                      <div class="flex flex-col overflow-hidden">
                          <span class="truncate font-medium">{{ model.displayValue }}</span>
                          <span class="text-xs text-gray-500 truncate">{{ model.displayMake || '' }}</span>
                      </div>
                      <span class="bg-gray-100 text-xs font-medium px-2 py-1 rounded-full text-gray-600 ml-2 shrink-0">
                        {{ props.getModelCount(model.value) }}
                      </span>
                    </Button>
                  </div>
                  <div v-else class="text-xs text-gray-500 py-4 text-center">
                    No models found for "{{ props.modelSearchKeyword.trim() }}".
                  </div>
                </template>

                <template v-else>
                  <template v-if="props.selectedMakesForMobileModelList.length > 0">
                    <template v-for="make in props.selectedMakesForMobileModelList" :key="make.value">
                      <div v-if="typeof make.value === 'string' && (props.modelsForExpandedMakes[make.value.toLowerCase()] || []).filter(model => (props.getModelCount(model.value) || 0) > 0).length > 0" class="mt-2">
                        <h4 class="text-lg font-semibold text-gray-500 mb-1 px-3">{{ make.displayValue }} Models</h4>
                        <Button
                          v-for="model in props.modelsForExpandedMakes[make.value.toLowerCase()]?.filter(model => (props.getModelCount(model.value) || 0) > 0)"
                          :key="model.value"
                          variant="ghost"
                          @click="emit('toggle-filter', { filterName: 'model', itemValue: model.value })"
                          class="w-full justify-between h-auto py-2 px-3 text-left flex items-center"
                          :class="{ 'bg-primary/10 text-primary font-semibold': props.isModelSelected(model.value) }"
                        >
                          <div class="flex flex-col overflow-hidden">
                              <span class="truncate font-medium">{{ props.getModelDisplayName(model.value) }}</span>
                              <span class="text-xs text-gray-500 truncate">{{ props.getModelBodyType(model.value) }}</span>
                          </div>
                          <span class="bg-gray-100 text-xs font-medium px-2 py-1 rounded-full text-gray-600 ml-2 shrink-0">
                            {{ props.getModelCount(model.value) }}
                          </span>
                        </Button>
                      </div>
                    </template>
                  </template>
                  <template v-else>
                    <template v-for="make in props.allMakesForMobileModelList" :key="make.value">
                      <div v-if="typeof make.value === 'string' && (props.modelsForExpandedMakes[make.value.toLowerCase()] || []).filter(model => (props.getModelCount(model.value) || 0) > 0).length > 0" class="mt-2">
                        <h4 class="text-xs font-semibold text-gray-500 mb-1 px-3">{{ make.displayValue }} Models</h4>
                         <Button
                          v-for="model in props.modelsForExpandedMakes[make.value.toLowerCase()]?.filter(model => (props.getModelCount(model.value) || 0) > 0)"
                          :key="model.value"
                          variant="ghost"
                          @click="emit('toggle-filter', { filterName: 'model', itemValue: model.value })"
                          class="w-full justify-between h-auto py-2 px-3 text-left flex items-center"
                          :class="{ 'bg-primary/10 text-primary font-semibold': props.isModelSelected(model.value) }"
                        >
                          <div class="flex flex-col overflow-hidden">
                              <span class="truncate font-medium">{{ props.getModelDisplayName(model.value) }}</span>
                              <span class="text-xs text-gray-500 truncate">{{ props.getModelBodyType(model.value) }}</span>
                          </div>
                          <span class="bg-gray-100 text-xs font-medium px-2 py-1 rounded-full text-gray-600 ml-2 shrink-0">
                            {{ props.getModelCount(model.value) }}
                          </span>
                        </Button>
                      </div>
                    </template>
                  </template>
                </template>
              </div>
            </CollapsibleContent>
          </Collapsible>
          
          <Collapsible 
            v-if="props.frontPageFiltersModel.length > 0"
            v-model:open="mobileSectionsOpen.badge" 
            class="border rounded-lg overflow-hidden mt-3"
          >
            <CollapsibleTrigger class="flex justify-between items-center w-full p-3 bg-gray-50 hover:bg-gray-100 transition-colors">
               <span class="font-medium truncate flex items-center">
                 <span class="mr-1">🏷️</span> Badge
               </span>
               <div class="flex items-center">
                 <template v-if="props.selectedBadges.length > 0">
                   <span class="text-sm text-gray-500 mr-2">({{ props.selectedBadges.length }} selected)</span>
                 </template>
                 <ChevronDown class="h-4 w-4 transition-transform duration-200 text-gray-500" :class="{'rotate-180': mobileSectionsOpen.badge}" />
               </div>
            </CollapsibleTrigger>
            <CollapsibleContent class="p-3 space-y-2">
              <Button 
                variant="ghost" 
                @click="emit('clear-filter', 'badge')" 
                class="w-full justify-start h-auto py-2 px-3 text-left"
                :class="{ 'bg-primary/10 text-primary font-semibold': props.selectedBadges.length === 0 }"
              >
                <div>
                  <div>All Badges <span class="text-xs text-gray-500">(no filter)</span></div>
                </div>
              </Button>
              
              <div>
                <div class="text-xs text-gray-500 mb-2 mt-1">
                  <template v-if="props.frontPageFiltersModel.length === 1 && props.getSelectedModel">
                    Badge options for {{ props.getSelectedModel }}:
                  </template>
                  <template v-else-if="props.frontPageFiltersModel.length > 1">
                    Badges for {{ props.frontPageFiltersModel.length }} selected models:
                  </template>
                  <template v-else> <!-- Should ideally not be reached due to parent v-if -->
                    Available badges:
                  </template>
                </div>
                <Button 
                  v-for="badge in allAvailableBadges"
                  :key="badge.value"
                  variant="ghost" 
                  @click.stop="emit('toggle-filter', { filterName: 'badge', itemValue: badge.value })" 
                  class="w-full justify-between h-auto py-2 px-3 text-left flex items-center"
                  :class="{ 'bg-primary/10 text-primary font-semibold': props.isBadgeSelected(badge.value) }"
                >
                  <div class="flex items-center">
                    <span v-if="props.isBadgeSelected(badge.value)" class="mr-1">✓</span>
                    <span>{{ badge.displayValue }}</span>
                  </div>
                  <span class="bg-gray-100 text-xs font-medium px-2 py-1 rounded-full text-gray-600 ml-2">
                    {{ badge.count }}
                  </span>
                </Button>
                <div v-if="allAvailableBadges.length === 0" class="text-xs text-gray-400 italic text-center py-2">
                  No badges available for the selected model(s).
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          <div class="border rounded-lg p-3"> 
             <div class="flex justify-between items-center my-2 mx-2 mt-0">
               <Label for="mobile-search" class="tracking-tight text-sm font-medium">Search Keywords</Label>
               <span v-if="props.searchKeyword" class="bg-gray-100 text-xs font-medium px-2 py-1 rounded-full text-gray-600 ml-2">
                 {{ props.navSearchFilteredCount }} found
               </span>
             </div>

             <div class="flex items-center space-x-2">
              <div class="relative flex-1">
                <Input
                  id="mobile-search"
                  :modelValue="props.searchKeyword"
                  @update:modelValue="emit('update:searchKeyword', $event)"
                  class="w-full pl-9 pr-8 border-gray-200 focus:border-primary focus:ring-primary rounded-lg bg-gray-50 transition-all duration-200 h-10"
                  placeholder="e.g. color, feature"
                  @keyup.enter="emit('apply-keyword-search')"
                />
                <span class="absolute start-0 inset-y-0 flex items-center justify-center px-2">
                  <Search class="size-6 text-muted-foreground" />
                </span>
                <div v-if="props.searchKeyword" class="absolute inset-y-0 right-2 flex items-center">
                  <button
                    @click="() => { emit('update:searchKeyword', ''); emit('clear-filter', 'search_keywords'); }"
                    type="button"
                    class="text-gray-400 hover:text-gray-600 transition-colors duration-200 focus:outline-none"
                    aria-label="Clear search"
                  >
                    <X class="h-4 w-4" />
                  </button>
                </div>
              </div>
              <Button 
                variant="default" 
                size="sm" 
                class="h-10 bg-black text-white hover:bg-gray-800" 
                @click="emit('update:open', false)"
              >
                <NuxtLink
                  :to="{ path: '/cars-for-sale', query: props.currentFilterQuery }"
                  class="block w-full text-center"
                >
                  Go
                </NuxtLink>
              </Button>
            </div>
           </div>
        </div>
      </ScrollArea>
      
      <SheetFooter class="p-4 border-t border-gray-200 bg-gray-50">
        <div class="flex w-full gap-2 items-center">
          <Button @click="emit('update:open', false)" class="w-full">
             <NuxtLink
              :to="{ path: '/cars-for-sale', query: props.currentFilterQuery }"
              class="block w-full text-center"
             >
               Show {{ props.navSearchFilteredCount }} cars
             </NuxtLink>
          </Button>
        </div>
      </SheetFooter>
    </SheetContent>
  </Sheet>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { ChevronDown, Search, Filter, X } from 'lucide-vue-next'
import { Input } from '~/layers/base/components/ui/input'
import { Button } from '~/layers/base/components/ui/button'
import { Label } from '~/layers/base/components/ui/label'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetFooter } from '~/layers/base/components/ui/sheet'
import { ScrollArea } from '~/layers/base/components/ui/scroll-area'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/layers/base/components/ui/collapsible'
import type { FilterOption } from '../../../types'

// Define badge filter type locally
interface Badge {
  value: string;
  displayValue: string;
  count: number;
}

// Define Props
const props = defineProps<{
  open: boolean
  activeFiltersCount: number
  navSearchFilteredCount: number
  currentFilterQuery: Record<string, any>

  bodyTypes: FilterOption[]
  bodyTypesCount: number
  getSelectedBodyType: string
  isBodyTypeSelected: (value: string) => boolean
  getBodyTypeCount: (value: string) => number

  makes: FilterOption[]
  makesCount: number
  getSelectedMake: string
  isMakeSelected: (value: string) => boolean
  getMakeCount: (value: string) => number

  modelsCount: number
  getSelectedModel: string
  isModelSelected: (value: string) => boolean
  getModelCount: (value: string) => number
  getModelDisplayName: (value: string) => string
  getModelBodyType: (value: string) => string
  globallyFilteredModels: FilterOption[]
  modelsForExpandedMakes: Record<string, FilterOption[]>
  selectedMakesForMobileModelList: FilterOption[]
  allMakesForMobileModelList: FilterOption[]


  searchKeyword: string
  modelSearchKeyword: string

  frontPageFiltersModel: string[]
  availableBadgesForModel: (modelValue: string) => Badge[]
  selectedBadges: string[]
  isBadgeSelected: (badgeValue: string) => boolean
}>()

// Define Emits
const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'update:searchKeyword', value: string): void
  (e: 'update:modelSearchKeyword', value: string): void
  (e: 'toggle-filter', payload: { filterName: 'body' | 'make' | 'model' | 'badge', itemValue: string }): void
  (e: 'clear-filter', filterName: 'body' | 'make' | 'model' | 'badge' | 'search_keywords'): void
  (e: 'apply-keyword-search'): void
  (e: 'handle-model-search'): void
}>()

// Local state for mobile collapsible sections
const mobileSectionsOpen = ref({
  body: true, // Open by default
  make: false,
  model: false,
  badge: false,
})

// Computed property to get all available badges for selected models
const allAvailableBadges = computed(() => {
  if (!props.frontPageFiltersModel || props.frontPageFiltersModel.length === 0) {
    return [];
  }
  const badgesMap = new Map<string, Badge>();
  props.frontPageFiltersModel.forEach(modelValue => {
    const modelBadges = props.availableBadgesForModel(modelValue);
    modelBadges.forEach(badge => {
      if (badgesMap.has(badge.value)) {
        const existingBadge = badgesMap.get(badge.value)!;
        // Sum counts and keep the displayValue from the first encounter
        badgesMap.set(badge.value, {
          ...existingBadge,
          count: existingBadge.count + badge.count
        });
      } else {
        badgesMap.set(badge.value, badge);
      }
    });
  });
  // Sort badges alphabetically by displayValue for consistent ordering
  return Array.from(badgesMap.values()).sort((a, b) => a.displayValue.localeCompare(b.displayValue));
});

// Watchers to open sections if they have selections
// This logic will be handled by the parent component NavSearchFilters.vue based on filter changes.
// The child component does not need to manage this itself.

</script> 